import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, apiFetch } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Send, Search, Inbox, UserCheck, Tag, Lock, BarChart3, Settings, Star, Clock } from "lucide-react";

const STATUSES = ["open", "pending", "waiting_customer", "resolved", "closed"];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const PRIORITY_COLOR = { low: "text-slate-400", normal: "text-sky-300", high: "text-amber-400", urgent: "text-red-400" };
const STATUS_COLOR = { open: "bg-[#0055FF]/20 text-[#7aa2ff]", pending: "bg-amber-500/15 text-amber-300", waiting_customer: "bg-purple-500/15 text-purple-300", resolved: "bg-emerald-500/15 text-emerald-300", closed: "bg-white/10 text-slate-400" };

export default function TicketInbox() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [list, setList] = useState({ tickets: [], total: 0, page: 1, pages: 1 });
  const [filters, setFilters] = useState({ status: "", priority: "", q: "", assignee: "", page: 1 });
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState("");
  const [denied, setDenied] = useState(false);
  const [view, setView] = useState("inbox");
  const [analytics, setAnalytics] = useState(null);
  const [agents, setAgents] = useState([]);
  const [slaCfg, setSlaCfg] = useState(null);

  const loadAnalytics = useCallback(async () => {
    try {
      setAnalytics(await apiFetch("/admin/analytics/tickets?days=14"));
      setAgents(await apiFetch("/admin/analytics/agents"));
      setSlaCfg(await apiFetch("/admin/sla/config"));
    } catch (e) { toast.error(e.message); }
  }, []);

  const saveSla = async (patch) => {
    try { const r = await apiFetch("/admin/sla/config", { method: "PUT", body: patch }); setSlaCfg(r); toast.success("SLA settings saved"); }
    catch (e) { toast.error(e.message); }
  };

  const loadStats = useCallback(async () => { try { setStats(await apiFetch("/admin/tickets/stats")); } catch { setDenied(true); } }, []);
  const loadList = useCallback(async () => {
    const qs = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
    qs.set("page_size", "15");
    try { setList(await apiFetch(`/admin/tickets?${qs.toString()}`)); } catch { setDenied(true); }
  }, [filters]);

  useEffect(() => { if (user === null) navigate("/login"); }, [user, navigate]);
  useEffect(() => { if (user) { loadStats(); loadList(); } }, [user, loadStats, loadList]);

  const openTicket = async (id) => { try { setSelected(await apiFetch(`/admin/tickets/${id}`)); } catch (e) { toast.error(e.message); } };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy("reply");
    try {
      await apiFetch(`/admin/tickets/${selected.id}/reply`, { method: "POST", body: { body: reply.trim(), internal } });
      toast.success(internal ? "Internal note added" : "Reply sent to customer");
      setReply(""); setInternal(false); await openTicket(selected.id); loadList(); loadStats();
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };

  const patch = async (updates) => {
    try { await apiFetch(`/admin/tickets/${selected.id}`, { method: "PATCH", body: updates });
      await openTicket(selected.id); loadList(); loadStats();
    } catch (e) { toast.error(e.message); }
  };

  const claim = async () => {
    try { await apiFetch(`/admin/tickets/${selected.id}/claim`, { method: "POST" }); toast.success("Ticket claimed"); await openTicket(selected.id); loadList(); }
    catch (e) { toast.error(e.message); }
  };

  if (denied) return <div className="min-h-screen flex items-center justify-center text-slate-400" data-testid="tickets-denied">Admin access required.</div>;
  if (!user || !stats) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="animate-spin mr-2" /> Loading…</div>;

  return (
    <div className="min-h-screen relative">
      <div className="pointer-events-none absolute -top-40 left-0 w-[40rem] h-[40rem] bg-[#0055FF]/10 blur-[150px] rounded-full" />
      <header className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <span className="font-display text-xl font-bold flex items-center gap-2"><Inbox size={20} className="text-[#00F0FF]" /> Ticket Inbox</span>
        <div className="flex items-center gap-4">
          <button onClick={() => { const v = view === "inbox" ? "analytics" : "inbox"; setView(v); if (v === "analytics" && !analytics) loadAnalytics(); }} className="font-mono text-xs uppercase tracking-widest text-[#00F0FF] hover:text-white inline-flex items-center gap-1.5" data-testid="tickets-view-toggle"><BarChart3 size={14} /> {view === "inbox" ? "Analytics" : "Inbox"}</button>
          <Link to="/admin" className="font-mono text-xs uppercase tracking-widest text-slate-400 hover:text-white inline-flex items-center gap-2"><ArrowLeft size={14} /> Admin</Link>
        </div>
      </header>

      {view === "analytics" ? (
        <main className="relative z-10 max-w-7xl mx-auto px-6 pb-24" data-testid="tickets-analytics">
          {!analytics ? <div className="flex items-center gap-2 text-slate-400 py-20"><Loader2 className="animate-spin" /> Loading analytics…</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <Stat label="CSAT average" value={analytics.csat.average != null ? `${analytics.csat.average}/5` : "—"} />
                <Stat label="CSAT responses" value={analytics.csat.responses} />
                <Stat label="Avg 1st response" value={analytics.avg_first_response_min != null ? `${analytics.avg_first_response_min}m` : "—"} />
                <Stat label="Avg resolution" value={analytics.avg_resolution_min != null ? `${analytics.avg_resolution_min}m` : "—"} />
              </div>
              {/* Timeseries */}
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 mb-6" data-testid="analytics-timeseries">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-4">Created vs resolved (14 days)</p>
                <div className="flex items-end gap-1 h-32">
                  {analytics.timeseries.map((d) => {
                    const max = Math.max(1, ...analytics.timeseries.map((x) => Math.max(x.created, x.resolved)));
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${d.date}: ${d.created} created, ${d.resolved} resolved`}>
                        <div className="w-full flex items-end gap-0.5 justify-center">
                          <div className="w-1/2 bg-[#0055FF] rounded-t" style={{ height: `${(d.created / max) * 100}px` }} />
                          <div className="w-1/2 bg-emerald-400 rounded-t" style={{ height: `${(d.resolved / max) * 100}px` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-2"><span className="text-[#0055FF]">■</span> Created &nbsp; <span className="text-emerald-400">■</span> Resolved</p>
              </div>
              {/* Agent performance */}
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 mb-6" data-testid="analytics-agents">
                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-3">Agent performance</p>
                {agents.length === 0 ? <p className="text-slate-500 text-sm">No assigned tickets yet.</p> : (
                  <div className="overflow-x-auto"><table className="w-full text-sm">
                    <thead><tr className="text-slate-500 text-xs text-left"><th className="py-2">Agent</th><th>Handled</th><th>Resolved</th><th>Avg 1st resp</th><th>Avg resolution</th><th>CSAT</th></tr></thead>
                    <tbody>{agents.map((a) => (
                      <tr key={a.assignee_id} className="border-t border-white/5" data-testid={`agent-row-${a.assignee_id}`}>
                        <td className="py-2 text-white">{a.name}</td><td>{a.handled}</td><td>{a.resolved}</td>
                        <td>{a.avg_first_response_min != null ? `${a.avg_first_response_min}m` : "—"}</td>
                        <td>{a.avg_resolution_min != null ? `${a.avg_resolution_min}m` : "—"}</td>
                        <td>{a.csat_average != null ? `${a.csat_average}★ (${a.csat_responses})` : "—"}</td>
                      </tr>
                    ))}</tbody>
                  </table></div>
                )}
              </div>
              {/* SLA config */}
              {slaCfg && (
                <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="sla-config">
                  <p className="font-mono text-[11px] uppercase tracking-widest text-slate-500 mb-3 flex items-center gap-1.5"><Settings size={13} /> Business-hours SLA</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={slaCfg.business_hours_enabled} onChange={(e) => saveSla({ business_hours_enabled: e.target.checked })} data-testid="sla-bh-toggle" /> Business hours only</label>
                    <label className="flex items-center gap-2 text-sm text-slate-300"><Clock size={13} /> <input type="number" min="0" max="23" value={slaCfg.start_hour} onChange={(e) => saveSla({ start_hour: parseInt(e.target.value) })} className="w-16 bg-black/40 border border-white/10 rounded px-2 py-1" data-testid="sla-start" /> to <input type="number" min="1" max="24" value={slaCfg.end_hour} onChange={(e) => saveSla({ end_hour: parseInt(e.target.value) })} className="w-16 bg-black/40 border border-white/10 rounded px-2 py-1" data-testid="sla-end" /></label>
                    <input value={slaCfg.timezone} onChange={(e) => setSlaCfg({ ...slaCfg, timezone: e.target.value })} onBlur={(e) => saveSla({ timezone: e.target.value })} className="bg-black/40 border border-white/10 rounded px-2 py-1 text-sm" placeholder="Timezone (IANA)" data-testid="sla-tz" />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-2">SLA due dates are calculated within business hours in the configured timezone. Targets (min): urgent 60/240, high 240/1440, normal 480/2880, low 1440/4320.</p>
                </div>
              )}
            </>
          )}
        </main>
      ) : (
      <main className="relative z-10 max-w-7xl mx-auto px-6 pb-24">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-3" data-testid="tickets-stats">
          <Stat label="Total" value={stats.total} />
          <Stat label="Unassigned" value={stats.unassigned} accent />
          {STATUSES.map((s) => <Stat key={s} label={s.replace("_", " ")} value={stats.by_status[s]} />)}
        </div>
        {/* SLA metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="tickets-sla-stats">
          <Stat label="SLA compliance" value={`${stats.sla?.compliance_pct ?? 100}%`} />
          <Stat label="Avg 1st response" value={stats.sla?.avg_first_response_min != null ? `${stats.sla.avg_first_response_min}m` : "—"} />
          <Stat label="Avg resolution" value={stats.sla?.avg_resolution_min != null ? `${stats.sla.avg_resolution_min}m` : "—"} />
          <Stat label="Response breached" value={stats.sla?.response_breached ?? 0} accent />
          <Stat label="Escalated" value={stats.sla?.escalated ?? 0} accent />
        </div>

        <div className="grid lg:grid-cols-5 gap-5">
          {/* List + filters */}
          <div className="lg:col-span-2">
            <div className="flex flex-wrap gap-2 mb-3">
              <div className="relative flex-1 min-w-[160px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))} placeholder="Search…" className="w-full bg-black/40 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#0055FF]" data-testid="tickets-search" />
              </div>
              <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value, page: 1 }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm outline-none" data-testid="tickets-filter-status">
                <option value="">All status</option>{STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <select value={filters.priority} onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value, page: 1 }))} className="bg-black/40 border border-white/10 rounded-lg px-2 py-2 text-sm outline-none" data-testid="tickets-filter-priority">
                <option value="">All priority</option>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] divide-y divide-white/5 max-h-[70vh] overflow-auto" data-testid="tickets-list">
              {list.tickets.length === 0 && <p className="p-5 text-slate-500 text-sm">No tickets match.</p>}
              {list.tickets.map((t) => (
                <button key={t.id} onClick={() => openTicket(t.id)} className={`block w-full text-left p-4 hover:bg-white/5 transition-colors duration-200 ${selected?.id === t.id ? "bg-white/5" : ""}`} data-testid={`ticket-row-${t.number}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm text-white truncate">{t.subject}</span>
                    <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full shrink-0 ${STATUS_COLOR[t.status]}`}>{t.status.replace("_", " ")}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
                    <span>#{t.number}</span>·<span className="truncate">{t.requester_email}</span>·<span className={`uppercase font-mono ${PRIORITY_COLOR[t.priority]}`}>{t.priority}</span>
                    {t.assignee_name && <span className="text-[#00F0FF]">· {t.assignee_name}</span>}
                    {t.sla_state?.escalated && <span className="text-red-400 font-mono">· escalated</span>}
                    {t.sla_state?.response_breached && <span className="text-red-400 font-mono">· SLA breach</span>}
                    {!t.sla_state?.response_breached && t.sla_state?.at_risk && !t.sla_state?.resolved && <span className="text-amber-400 font-mono">· at risk</span>}
                  </div>
                </button>
              ))}
            </div>
            {list.pages > 1 && (
              <div className="flex items-center justify-between mt-3 text-sm text-slate-400">
                <button disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))} className="disabled:opacity-30" data-testid="tickets-prev">Prev</button>
                <span>Page {list.page} / {list.pages}</span>
                <button disabled={filters.page >= list.pages} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))} className="disabled:opacity-30" data-testid="tickets-next">Next</button>
              </div>
            )}
          </div>

          {/* Detail */}
          <div className="lg:col-span-3">
            {!selected ? (
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-10 text-center text-slate-500" data-testid="ticket-empty">Select a ticket to view the conversation.</div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5" data-testid="ticket-detail">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h2 className="font-display font-bold text-xl">{selected.subject}</h2>
                    <p className="text-slate-500 text-xs mt-0.5">#{selected.number} · {selected.requester_email}</p>
                  </div>
                  <button onClick={claim} className="text-xs border border-white/15 rounded-lg px-3 py-1.5 hover:border-[#0055FF] inline-flex items-center gap-1.5 shrink-0" data-testid="ticket-claim"><UserCheck size={13} /> Claim</button>
                </div>

                {/* Controls */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <select value={selected.status} onChange={(e) => patch({ status: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none" data-testid="ticket-status-select">
                    {STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                  </select>
                  <select value={selected.priority} onChange={(e) => patch({ priority: e.target.value })} className="bg-black/40 border border-white/10 rounded-lg px-2 py-1.5 text-xs outline-none" data-testid="ticket-priority-select">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <TagEditor tags={selected.tags || []} onSave={(tags) => patch({ tags })} />
                </div>

                {/* SLA panel */}
                {selected.sla_state && (
                  <div className="rounded-xl border border-white/10 bg-black/30 p-3 mb-4 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="ticket-sla">
                    <div><p className="text-slate-500">First response</p><p className={selected.sla_state.answered ? "text-emerald-400" : selected.sla_state.response_breached ? "text-red-400" : "text-slate-200"}>{selected.sla_state.answered ? "answered" : selected.sla_state.response_breached ? "BREACHED" : `${selected.sla_state.minutes_to_response_due}m left`}</p></div>
                    <div><p className="text-slate-500">Resolution</p><p className={selected.sla_state.resolved ? "text-emerald-400" : selected.sla_state.resolution_breached ? "text-red-400" : "text-slate-200"}>{selected.sla_state.resolved ? "resolved" : selected.sla_state.resolution_breached ? "BREACHED" : `${selected.sla_state.minutes_to_resolution_due}m left`}</p></div>
                    <div><p className="text-slate-500">Escalated</p><p className={selected.sla_state.escalated ? "text-red-400" : "text-slate-400"}>{selected.sla_state.escalated ? "yes" : "no"}</p></div>
                    <div><p className="text-slate-500">Assignee</p><p className="text-slate-200 truncate">{selected.assignee_name || "unassigned"}</p></div>
                  </div>
                )}

                {/* Conversation timeline */}
                <div className="space-y-3 max-h-[42vh] overflow-auto mb-4" data-testid="ticket-timeline">
                  {selected.messages.map((m) => (
                    <div key={m.id} className={`rounded-xl p-3 border ${m.internal ? "border-amber-500/30 bg-amber-500/5" : m.author_type === "agent" ? "border-[#0055FF]/30 bg-[#0055FF]/5" : "border-white/10 bg-black/30"}`} data-testid="ticket-message">
                      <div className="flex items-center gap-2 mb-1 text-[11px] text-slate-500">
                        <span className="text-white font-medium">{m.author_name}</span>
                        <span className="uppercase font-mono">{m.author_type}</span>
                        {m.internal && <span className="text-amber-400 inline-flex items-center gap-1"><Lock size={10} /> internal</span>}
                      </div>
                      <p className="text-sm text-slate-200 whitespace-pre-wrap">{m.body}</p>
                      {m.attachments?.length > 0 && <p className="text-[11px] text-slate-500 mt-1">{m.attachments.length} attachment(s)</p>}
                    </div>
                  ))}
                </div>

                {/* Reply */}
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3} placeholder={internal ? "Write an internal note (not sent to the customer)…" : "Reply to the customer…"} className="w-full resize-none bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-[#0055FF]" data-testid="ticket-reply-input" />
                <div className="flex items-center justify-between mt-2">
                  <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="ticket-internal-toggle" /> Internal note
                  </label>
                  <button onClick={sendReply} disabled={busy === "reply" || !reply.trim()} className="inline-flex items-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-xl disabled:opacity-40 hover:bg-[#0047d6] transition-colors duration-300" data-testid="ticket-send-reply">
                    {busy === "reply" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {internal ? "Add note" : "Send reply"}
                  </button>
                </div>

                {/* Audit */}
                {selected.audit?.length > 0 && (
                  <details className="mt-4"><summary className="text-xs text-slate-500 cursor-pointer" data-testid="ticket-audit-toggle">Audit history ({selected.audit.length})</summary>
                    <div className="mt-2 space-y-1">
                      {selected.audit.map((a) => <p key={a.id} className="text-[11px] text-slate-500 font-mono">{a.created_at.slice(0, 19).replace("T", " ")} · {a.action} · {a.actor}</p>)}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3 ${accent && value > 0 ? "border-amber-500/40 bg-amber-500/5" : "border-white/10 bg-[#0a0a0a]"}`}>
      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p className="font-display font-extrabold text-2xl">{value}</p>
    </div>
  );
}

function TagEditor({ tags, onSave }) {
  const [val, setVal] = useState("");
  const add = () => { if (val.trim()) { onSave([...tags, val.trim()]); setVal(""); } };
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="ticket-tags">
      <Tag size={13} className="text-slate-500" />
      {tags.map((t, i) => <span key={i} className="text-[11px] bg-white/10 rounded-full px-2 py-0.5 text-slate-300">{t} <button onClick={() => onSave(tags.filter((_, j) => j !== i))} className="text-slate-500 hover:text-red-400">×</button></span>)}
      <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="add tag" className="bg-transparent border border-white/10 rounded-full px-2 py-0.5 text-[11px] w-20 outline-none focus:border-[#0055FF]" data-testid="ticket-tag-input" />
    </div>
  );
}
